'use client';

import { IconButton } from '@chakra-ui/react';
import {
  BaseEdge,
  type EdgeProps,
  getBezierPath,
} from '@xyflow/react';
import { memo, useCallback, useState } from 'react';
import { LuPlus } from 'react-icons/lu';

import { EDGE_STYLE } from '@features/workflows/constants';
import { useWorkflowCallbacks } from '@features/workflows/WorkflowContext';

const WorkflowEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}: EdgeProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { onEdgeInsert } = useWorkflowCallbacks();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const handleInsert = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdgeInsert(id, { x: labelX, y: labelY });
    },
    [id, onEdgeInsert, labelX, labelY],
  );

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
      />
      <BaseEdge id={id} path={edgePath} style={EDGE_STYLE} />
      {isHovered && (
        <foreignObject
          width={24}
          height={24}
          x={labelX - 12}
          y={labelY - 12}
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <IconButton
            size="2xs"
            borderRadius="full"
            bg="surface.background"
            borderWidth="1px"
            borderColor="border.light"
            boxShadow="sm"
            color="text.secondary"
            _hover={{ color: 'text.primary', bg: 'surface.hover' }}
            onClick={handleInsert}
            aria-label="Insert node"
            minW="24px"
            h="24px"
          >
            <LuPlus size={12} />
          </IconButton>
        </foreignObject>
      )}
    </g>
  );
};

export default memo(WorkflowEdge);
